"use client";

import React from "react";
import { Mail, MessageSquare, Clock, Send, Loader, CircleAlert, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
const supportInfo = [{
  icon: Mail,
  title: "Email Support",
  detail: "info@gotmeds.io",
  subtext: "Response time: Under 1 hour"
}, {
  icon: MessageSquare,
  title: "Live Patient Chat",
  detail: "Available in Patient Portal",
  subtext: "Instant medical coordination"
}, {
  icon: Clock,
  title: "Service Hours",
  detail: "24/7 Delivery & Support",
  subtext: "Atlanta local time (EST)"
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="224984" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-lg bg-secondary/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-secondary" />
        </div>
        <h3 className="text-2xl font-bold font-default text-foreground">Request Received</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary/50 hover:bg-primary/10">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-default">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Julian Atlanta" className="bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-primary/30 h-12 rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-default">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="patient@example.com" className="bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-primary/30 h-12 text-left rounded-lg" dir="ltr" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="subject" rules={{
      required: "Subject is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-default">Subject</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Prescription Verification" className="bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-primary/30 h-12 rounded-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-default">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="How can our medical logistics team assist you today?" className="min-h-[160px] bg-background/50 border-border/50 focus-visible:border-primary focus-visible:ring-primary/30 resize-y rounded-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold font-default bg-gradient-to-r from-primary to-chart-3 hover:opacity-90 transition-opacity shadow-lg shadow-primary/20 border-0 rounded-lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Processing...
          </> : <>
            <Send className="mr-2 size-5" />
            Send Secure Message
          </>}
      </Button>
    </Form>;
}
export default function ContactUs() {
  return <section id="direct-assistance" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-lg blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/10 rounded-lg blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="text-3xl md:text-5xl font-bold font-default mb-4 tracking-tight">
            Direct <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Assistance</span>
          </h2>
          <p className="text-muted-foreground text-lg font-default">
            Our dedicated patient support team and medical coordinators are available 24/7 to ensure your healthcare needs are met with precision.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {}
          <div className="lg:col-span-2">
            <Card className="bg-card/40 backdrop-blur-md border-border/50 shadow-2xl rounded-xl">
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl font-bold font-default">Secure Inquiry</CardTitle>
                <CardDescription className="font-default">Submit your request below and a coordinator will contact you shortly.</CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formKey="direct_assistance_form_patient_support" formId="direct_assistance_form_patient_support" sectionName="ContactUs" defaultValues={{
                name: "",
                email: "",
                subject: "",
                message: ""
              }} submitText="Send Secure Message" successMessage="Your inquiry has been received. A medical coordinator will reach out to you within the hour." errorMessage="There was an error processing your request. Please try again or call our support line.">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

          {}
          <div className="space-y-6">
            <Card className="bg-card/40 backdrop-blur-md border-border/50 h-full rounded-xl">
              <CardHeader>
                <CardTitle className="text-xl font-bold font-default">Concierge Support</CardTitle>
                <CardDescription className="font-default">Direct channels for patients and medical partners.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {supportInfo.map((info, i) => <div key={i} data-index={i} className="flex items-start gap-4">
                    <div className="size-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 shadow-sm">
                      <info.icon className="size-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-foreground font-default">{info.title}</h4>
                      <p className="text-foreground/80 mt-1 font-mono text-sm" dir={info.icon === Mail ? "ltr" : "ltr"}>
                        {info.detail}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1 font-default">{info.subtext}</p>
                    </div>
                  </div>)}

                <div className="pt-8 mt-8 border-t border-border/50">
                  <h4 className="font-bold text-foreground font-default mb-4">Atlanta Operations</h4>
                  <p className="text-muted-foreground text-sm leading-relaxed font-default">
                    GOTMEDS.IO Logistics Center<br />
                    Serving Greater Atlanta & Buckhead<br />
                    <span className="text-primary font-semibold">24/7 Medical Dispatch Active</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}